// Hook for managing GIF sessions in browser-local storage.
import { useState, useEffect, useCallback, useMemo, useRef } from 'react'
import type { GifSession, GifSessionFrame } from '@/lib/local-types'
import { LOCAL_LIBRARY_ID, readLocalJson, rememberLocalMode } from '@/lib/browser-persistence'
import { getAllSessions, saveAllSessions } from '@/features/gif/gif-sessions-store'
import { DEFAULT_GIF_SETTINGS } from '@/features/gif/types'
import { toast } from 'sonner'

export type GifSessionSortOption = 'date-desc' | 'date-asc' | 'name-asc' | 'name-desc' | 'frames-desc'

// Legacy localStorage key (pre-IndexedDB). Kept only for one-time migration.
const GIF_SESSIONS_KEY = 'framex.gifSessions'
const GIF_SESSIONS_CHANGE_EVENT = 'gif-sessions-changed'

let instanceCounter = 0

function notifySessionsChanged(sourceInstanceId: number) {
  window.dispatchEvent(new CustomEvent(GIF_SESSIONS_CHANGE_EVENT, {
    detail: { sourceInstanceId }
  }))
}

function defaultSessionSettings(): GifSession['settings'] {
  return {
    playbackMode: DEFAULT_GIF_SETTINGS.playbackMode,
    frameRate: DEFAULT_GIF_SETTINGS.frameRate,
    quality: DEFAULT_GIF_SETTINGS.quality,
    maxWidth: DEFAULT_GIF_SETTINGS.maxWidth,
    loop: DEFAULT_GIF_SETTINGS.loop,
    defaultFrameDuration: DEFAULT_GIF_SETTINGS.defaultFrameDuration,
  }
}

// Load from IndexedDB, migrating any legacy localStorage sessions once.
async function loadStoredSessions(): Promise<GifSession[]> {
  let sessions = await getAllSessions()
  if (sessions.length === 0) {
    const legacy = readLocalJson<GifSession[]>(GIF_SESSIONS_KEY, [])
    if (Array.isArray(legacy) && legacy.length > 0) {
      try { await saveAllSessions(legacy) } catch { /* fall back to in-memory */ }
      sessions = legacy
    }
  }
  // Drop the legacy key so it can never overflow the localStorage quota again.
  try { localStorage.removeItem(GIF_SESSIONS_KEY) } catch { /* ignore */ }
  return sessions
}

// Persist to IndexedDB (async). Never throws into React render, a quota/IO
// failure surfaces as a toast instead of crashing the app.
function saveStoredSessions(sessions: GifSession[]): void {
  saveAllSessions(sessions)
    .then(() => rememberLocalMode())
    .catch((err) => {
      console.error('Failed to persist GIF sessions:', err)
      toast.error('Could not save GIF session', {
        description: 'Local storage error, your latest changes may not persist.',
      })
    })
}

function mergeSettings(settings?: Partial<GifSession['settings']>): GifSession['settings'] {
  return {
    ...defaultSessionSettings(),
    ...settings,
  }
}

interface UseGifSessionsReturn {
  sessions: GifSession[]
  isLoading: boolean
  isSyncing: boolean
  error: string | null
  selectedSession: GifSession | null
  createSession: (name: string, settings?: Partial<GifSession['settings']>) => Promise<GifSession | null>
  selectSession: (sessionId: string | null) => void
  renameSession: (sessionId: string, name: string) => Promise<boolean>
  deleteSession: (sessionId: string) => Promise<boolean>
  duplicateSession: (sessionId: string) => Promise<GifSession | null>
  updateSessionSettings: (sessionId: string, settings: Partial<GifSession['settings']>) => Promise<boolean>
  addFrameToSession: (sessionId: string, frame: {
    sourceFrameId: string
    imageData: string
    width: number
    height: number
    duration?: number
  }) => Promise<GifSessionFrame | null>
  removeFrameFromSession: (sessionId: string, frameId: string) => Promise<boolean>
  reorderFrames: (sessionId: string, frameIds: string[]) => Promise<boolean>
  syncSession: (sessionId: string, frames: Array<{
    id?: string
    sourceFrameId: string
    imageData: string
    order: number
    width: number
    height: number
    duration?: number
  }>, settings?: Partial<GifSession['settings']>) => Promise<GifSession | null>
  refreshSessions: () => Promise<void>
  sortBy: GifSessionSortOption
  setSortBy: (sort: GifSessionSortOption) => void
  searchQuery: string
  setSearchQuery: (query: string) => void
  filteredSessions: GifSession[]
}

export function useGifSessions(): UseGifSessionsReturn {
  const [sessions, setSessions] = useState<GifSession[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [isSyncing, setIsSyncing] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [selectedSessionId, setSelectedSessionId] = useState<string | null>(null)
  const [sortBy, setSortBy] = useState<GifSessionSortOption>('date-desc')
  const [searchQuery, setSearchQuery] = useState('')
  const instanceIdRef = useRef<number>(0)

  useEffect(() => {
    instanceIdRef.current = ++instanceCounter
  }, [])

  const fetchSessions = useCallback(async () => {
    setIsLoading(true)
    setError(null)
    try {
      setSessions(await loadStoredSessions())
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to load GIF sessions'
      setError(message)
    } finally {
      setIsLoading(false)
    }
  }, [])

  const updateSessions = useCallback((updater: (current: GifSession[]) => GifSession[]) => {
    setSessions((current) => {
      const next = updater(current)
      saveStoredSessions(next)
      return next
    })
    notifySessionsChanged(instanceIdRef.current)
  }, [])

  useEffect(() => {
    fetchSessions()
  }, [fetchSessions])

  useEffect(() => {
    const handleSessionsChanged = (event: Event) => {
      const customEvent = event as CustomEvent<{ sourceInstanceId: number }>
      if (customEvent.detail?.sourceInstanceId === instanceIdRef.current) return
      fetchSessions()
    }

    window.addEventListener(GIF_SESSIONS_CHANGE_EVENT, handleSessionsChanged)
    return () => window.removeEventListener(GIF_SESSIONS_CHANGE_EVENT, handleSessionsChanged)
  }, [fetchSessions])

  const selectedSession = useMemo(() => {
    if (!selectedSessionId) return null
    return sessions.find(session => session.id === selectedSessionId) || null
  }, [sessions, selectedSessionId])

  const filteredSessions = useMemo(() => {
    let result = [...sessions]

    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase()
      result = result.filter(session => session.name.toLowerCase().includes(query))
    }

    result.sort((a, b) => {
      switch (sortBy) {
        case 'date-desc':
          return new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime()
        case 'date-asc':
          return new Date(a.updatedAt).getTime() - new Date(b.updatedAt).getTime()
        case 'name-asc':
          return a.name.localeCompare(b.name)
        case 'name-desc':
          return b.name.localeCompare(a.name)
        case 'frames-desc':
          return b.frames.length - a.frames.length
        default:
          return 0
      }
    })

    return result
  }, [sessions, searchQuery, sortBy])

  const createSession = useCallback(async (
    name: string,
    settings?: Partial<GifSession['settings']>
  ): Promise<GifSession | null> => {
    const now = new Date().toISOString()
    const session: GifSession = {
      id: crypto.randomUUID(),
      userId: LOCAL_LIBRARY_ID,
      name,
      frames: [],
      settings: mergeSettings(settings),
      totalSize: 0,
      createdAt: now,
      updatedAt: now,
    }

    updateSessions(current => [session, ...current])
    toast.success('GIF session created')
    return session
  }, [updateSessions])

  const selectSession = useCallback((sessionId: string | null) => {
    setSelectedSessionId(sessionId)
  }, [])

  const renameSession = useCallback(async (sessionId: string, name: string): Promise<boolean> => {
    updateSessions(current => current.map(session => (
      session.id === sessionId ? { ...session, name, updatedAt: new Date().toISOString() } : session
    )))
    toast.success('Session renamed')
    return true
  }, [updateSessions])

  const deleteSession = useCallback(async (sessionId: string): Promise<boolean> => {
    updateSessions(current => current.filter(session => session.id !== sessionId))
    if (selectedSessionId === sessionId) setSelectedSessionId(null)
    toast.success('Session deleted')
    return true
  }, [selectedSessionId, updateSessions])

  const duplicateSession = useCallback(async (sessionId: string): Promise<GifSession | null> => {
    const source = sessions.find(session => session.id === sessionId)
    if (!source) return null

    const now = new Date().toISOString()
    const duplicate: GifSession = {
      ...source,
      id: crypto.randomUUID(),
      name: `Copy of ${source.name}`,
      frames: source.frames.map(frame => ({ ...frame, id: crypto.randomUUID() })),
      createdAt: now,
      updatedAt: now,
    }

    updateSessions(current => [duplicate, ...current])
    toast.success('Session duplicated')
    return duplicate
  }, [sessions, updateSessions])

  const updateSessionSettings = useCallback(async (
    sessionId: string,
    settings: Partial<GifSession['settings']>
  ): Promise<boolean> => {
    updateSessions(current => current.map(session => (
      session.id === sessionId
        ? { ...session, settings: { ...session.settings, ...settings }, updatedAt: new Date().toISOString() }
        : session
    )))
    return true
  }, [updateSessions])

  const addFrameToSession = useCallback(async (
    sessionId: string,
    frame: {
      sourceFrameId: string
      imageData: string
      width: number
      height: number
      duration?: number
    }
  ): Promise<GifSessionFrame | null> => {
    const newFrame: GifSessionFrame = {
      id: crypto.randomUUID(),
      sourceFrameId: frame.sourceFrameId,
      blobUrl: frame.imageData,
      url: frame.imageData,
      order: sessions.find(session => session.id === sessionId)?.frames.length || 0,
      width: frame.width,
      height: frame.height,
      duration: frame.duration,
    }

    updateSessions(current => current.map(session => (
      session.id === sessionId
        ? {
            ...session,
            frames: [...session.frames, newFrame],
            totalSize: session.totalSize + frame.imageData.length,
            updatedAt: new Date().toISOString(),
          }
        : session
    )))
    return newFrame
  }, [sessions, updateSessions])

  const removeFrameFromSession = useCallback(async (sessionId: string, frameId: string): Promise<boolean> => {
    updateSessions(current => current.map(session => (
      session.id === sessionId
        ? {
            ...session,
            frames: session.frames.filter(frame => frame.id !== frameId).map((frame, index) => ({ ...frame, order: index })),
            updatedAt: new Date().toISOString(),
          }
        : session
    )))
    return true
  }, [updateSessions])

  const reorderFrames = useCallback(async (sessionId: string, frameIds: string[]): Promise<boolean> => {
    updateSessions(current => current.map(session => {
      if (session.id !== sessionId) return session
      const frameMap = new Map(session.frames.map(frame => [frame.id, frame]))
      const reordered = frameIds
        .map(id => frameMap.get(id))
        .filter((frame): frame is GifSessionFrame => Boolean(frame))
        .map((frame, index) => ({ ...frame, order: index }))
      return { ...session, frames: reordered, updatedAt: new Date().toISOString() }
    }))
    return true
  }, [updateSessions])

  const syncSession = useCallback(async (
    sessionId: string,
    frames: Array<{
      id?: string
      sourceFrameId: string
      imageData: string
      order: number
      width: number
      height: number
      duration?: number
    }>,
    settings?: Partial<GifSession['settings']>
  ): Promise<GifSession | null> => {
    setIsSyncing(true)
    try {
      let updatedSession: GifSession | null = null
      updateSessions(current => current.map(session => {
        if (session.id !== sessionId) return session
        updatedSession = {
          ...session,
          frames: frames.map((frame, index) => ({
            id: frame.id || crypto.randomUUID(),
            sourceFrameId: frame.sourceFrameId,
            blobUrl: frame.imageData,
            url: frame.imageData,
            order: frame.order ?? index,
            width: frame.width,
            height: frame.height,
            duration: frame.duration,
          })),
          settings: { ...session.settings, ...settings },
          totalSize: frames.reduce((sum, frame) => sum + frame.imageData.length, 0),
          updatedAt: new Date().toISOString(),
        }
        return updatedSession
      }))
      return updatedSession
    } finally {
      setIsSyncing(false)
    }
  }, [updateSessions])

  return {
    sessions,
    isLoading,
    isSyncing,
    error,
    selectedSession,
    createSession,
    selectSession,
    renameSession,
    deleteSession,
    duplicateSession,
    updateSessionSettings,
    addFrameToSession,
    removeFrameFromSession,
    reorderFrames,
    syncSession,
    refreshSessions: fetchSessions,
    sortBy,
    setSortBy,
    searchQuery,
    setSearchQuery,
    filteredSessions,
  }
}